import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import api from '../api';

export default function ImportCsvEleves({
  classeId = '',
  availableClasses = [],
  token,
  onImport,
  initialImportMode,
  lockedImportMode
}) {
  const inputRef = useRef();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [importMode, setImportMode] = useState(
    lockedImportMode || initialImportMode || (classeId ? 'selected' : 'fileclasses')
  );
  const [selectedClasseId, setSelectedClasseId] = useState(classeId || '');

  useEffect(() => {
    if (lockedImportMode) {
      setImportMode(lockedImportMode);
    }
  }, [lockedImportMode]);

  useEffect(() => {
    if (classeId) {
      setImportMode('selected');
      setSelectedClasseId(classeId);
      return;
    }

    if (initialImportMode && !lockedImportMode) {
      setImportMode(initialImportMode);
    }

    if (!selectedClasseId && availableClasses.length > 0) {
      setSelectedClasseId(availableClasses[0]._id);
    }
  }, [classeId, availableClasses, selectedClasseId, initialImportMode, lockedImportMode]);

  const isLockedToClass = Boolean(classeId);
  const effectiveImportMode = lockedImportMode || importMode;

  const selectedClasseLabel = useMemo(() => {
    return availableClasses.find((item) => item._id === selectedClasseId)?.nom || '';
  }, [availableClasses, selectedClasseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setDetails(null);

    const file = inputRef.current?.files?.[0];
    if (!file) {
      setError('Aucun fichier sélectionné');
      return;
    }

    if (effectiveImportMode === 'selected' && !selectedClasseId) {
      setError('Choisis une classe pour l’import ciblé');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('importMode', effectiveImportMode);
      formData.append('autoCreateClasses', effectiveImportMode === 'fileclasses' ? 'true' : 'false');

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (token) config.headers['x-seance-token'] = token;

      const endpoint = effectiveImportMode === 'selected'
        ? `/eleves/import/${encodeURIComponent(selectedClasseId)}`
        : '/eleves/import';

      const res = await api.post(endpoint, formData, config);
      const data = res.data || {};

      const createdCount = data.count ?? 0;
      const createdClassesCount = Array.isArray(data.classesCreated) ? data.classesCreated.length : 0;
      const importedInto = effectiveImportMode === 'selected' && selectedClasseLabel
        ? ` dans ${selectedClasseLabel}`
        : '';

      setMessage(
        `Import réussi : ${createdCount} élève(s) créé(s)${importedInto}` +
        (createdClassesCount > 0 ? `, ${createdClassesCount} classe(s) créée(s).` : '.')
      );
      setDetails({
        skipped: data.skipped ?? 0,
        skippedRows: data.skippedRows ?? [],
        classesCreated: data.classesCreated ?? [],
        classesResolved: data.classesResolved ?? []
      });

      if (onImport) onImport(data);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      console.error('Import error', err);
      const serverMsg = err?.response?.data?.error || err?.response?.data || err?.message;
      setError(serverMsg || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Import PRONOTE / CSV — séparateur <strong>;</strong> recommandé. Les colonnes reconnues sont
        <strong> classe / groupe / division</strong>, <strong>nom</strong>, <strong>prénom</strong>,
        <strong> matricule / identifiant / INE / id</strong> et éventuellement <strong>email / mail</strong>.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Deux modes sont possibles :
        <strong> importer dans une classe choisie</strong> ou <strong>utiliser la colonne classe du fichier</strong>
        pour créer / retrouver automatiquement les classes du prof.
      </Typography>

      {lockedImportMode === 'fileclasses' && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Mode PRONOTE activé : le fichier doit contenir une colonne <strong>classe</strong> et l’application créera
          automatiquement les classes manquantes avant d’importer les élèves.
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {!isLockedToClass && !lockedImportMode && (
          <TextField
            select
            label="Mode d'import"
            size="small"
            value={importMode}
            onChange={(event) => setImportMode(event.target.value)}
          >
            <MenuItem value="selected">Importer dans une classe choisie</MenuItem>
            <MenuItem value="fileclasses">Utiliser la colonne classe du fichier</MenuItem>
          </TextField>
        )}

        {(isLockedToClass || effectiveImportMode === 'selected') && (
          <TextField
            select
            label="Classe cible"
            size="small"
            value={selectedClasseId}
            onChange={(event) => setSelectedClasseId(event.target.value)}
            disabled={isLockedToClass}
            helperText={isLockedToClass ? 'Classe imposée par l’écran courant.' : 'Tous les élèves du fichier seront ajoutés à cette classe.'}
          >
            {availableClasses.length === 0 && <MenuItem value="">Aucune classe disponible</MenuItem>}
            {availableClasses.map((item) => (
              <MenuItem key={item._id} value={item._id}>{item.nom}</MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: 'block', marginBottom: 8 }} />

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {message && <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {details?.classesCreated?.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Classes créées : {details.classesCreated.map((item) => item.nom).join(', ')}
        </Alert>
      )}

      {details && details.skipped > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {details.skipped} ligne(s) ignorée(s). Exemples : {JSON.stringify(details.skippedRows)}
        </Alert>
      )}

      <Button type="submit" variant="contained" disabled={loading}>
        Importer le CSV
      </Button>
    </Box>
  );
}